<template>
  <div class="reward-grid-detail-config">
    <div class="rg-section">
      <h4 class="rg-section__title">Vollflächiger Hintergrund für Detailansicht</h4>

      <label class="rg-label">Aktionsanzeige</label>
      <segment-toggle
          v-model="fields.detail_background_type"
          :options="[
              {value: 'image', label: 'Hintergrundbild'},
              {value: 'color', label: 'Hintergrundfarbe'},
          ]"/>

      <template v-if="fields.detail_background_type === 'image'">
        <label class="rg-label">
          Bild hochladen
          <a class="fa fa-question-circle rg-tooltip-icon" v-tippy
             content="Vollflächiges Hintergrundbild für die Detailansicht."></a>
        </label>
        <image-upload-with-preview
            :existing-url="dbRecord.detail_background_image_url"
            v-model="fields.detail_background_image"
            :deleted.sync="fields.detail_background_image_delete"/>
      </template>

      <template v-else>
        <div style="max-width: 200px;">
          <label class="rg-label">Farbe auswählen</label>
          <input type="color"
                 name="reward_grid_detail_background_color"
                 class="rg-color-input"
                 v-model="fields.detail_background_color">
        </div>
      </template>
    </div>

    <div class="rg-section">
      <h4 class="rg-section__title">
        Titelbereich
        <a class="fa fa-question-circle rg-tooltip-icon" v-tippy
           content="Bild mit Verhältnis 2:1 (z.B. 2160 x 1080 px)."></a>
      </h4>

      <label class="rg-label">Aktionsanzeige</label>
      <segment-toggle
          v-model="fields.title_area_type"
          :options="[
              {value: 'image', label: 'Titelbild'},
              {value: 'text', label: 'Nur Text'},
          ]"/>

      <template v-if="fields.title_area_type === 'image'">
        <label class="rg-label">
          Bild hochladen
          <a class="fa fa-question-circle rg-tooltip-icon" v-tippy
             content="Titelbild für den Titelbereich."></a>
        </label>
        <image-upload-with-preview
            :existing-url="dbRecord.title_area_image_url"
            v-model="fields.title_area_image"
            :deleted.sync="fields.title_area_image_delete"/>
      </template>

      <template v-else>
        <label class="rg-label">
          Text eingeben
          <a class="fa fa-question-circle rg-tooltip-icon" v-tippy
             content="Titeltext für den Titelbereich."></a>
        </label>
        <ckeditor v-if="ckeditorReady" v-model="fields.title_area_text" :config="editorConfig()"></ckeditor>
      </template>
    </div>

    <div class="rg-section">
      <div class="rg-checkbox-row">
        <input type="checkbox"
               id="reward_grid_description_before_enabled"
               name="reward_grid_description_before_enabled"
               value="1"
               v-model="fields.description_before_enabled">
        <label for="reward_grid_description_before_enabled" class="rg-checkbox-label">Optionale Aktionsbeschreibung (vor Kacheln)</label>
      </div>

      <template v-if="fields.description_before_enabled">
        <label class="rg-label">Überschrift (optional)</label>
        <ckeditor v-if="ckeditorReady" v-model="fields.description_before_heading" :config="editorConfig()"></ckeditor>

        <label class="rg-label">Fließtext (optional)</label>
        <ckeditor v-if="ckeditorReady" v-model="fields.description_before_body" :config="editorConfig()"></ckeditor>
      </template>
    </div>

    <reward-grid-tiles
        ref="tiles"
        :db-record="tilesDbRecord"
        :part-of-reward-grid-campaigns="partOfRewardGridCampaigns"
        :content-hub-items="contentHubItems"/>

    <div class="rg-section">
      <div class="rg-checkbox-row">
        <input type="checkbox"
               id="reward_grid_description_after_enabled"
               name="reward_grid_description_after_enabled"
               value="1"
               v-model="fields.description_after_enabled">
        <label for="reward_grid_description_after_enabled" class="rg-checkbox-label">Optionale Aktionsbeschreibung (nach Kacheln)</label>
      </div>

      <template v-if="fields.description_after_enabled">
        <label class="rg-label">Überschrift (optional)</label>
        <ckeditor v-if="ckeditorReady" v-model="fields.description_after_heading" :config="editorConfig()"></ckeditor>

        <label class="rg-label">Fließtext (optional)</label>
        <ckeditor v-if="ckeditorReady" v-model="fields.description_after_body" :config="editorConfig()"></ckeditor>
      </template>
    </div>
  </div>
</template>

<script>
import SegmentToggle from '../../common/segment-toggle/SegmentToggle.vue';
import ImageUploadWithPreview from '../../common/image-upload-with-preview/ImageUploadWithPreview.vue';
import RewardGridTiles from './RewardGridTiles.vue';

export default {
  name: 'RewardGridDetailConfig',
  components: {SegmentToggle, ImageUploadWithPreview, RewardGridTiles},
  props: {
    campaignId: {type: [Number, String], required: true},
    dbRecord: {type: Object, default: () => ({})},
    tilesDbRecord: {type: Array, default: () => []},
    partOfRewardGridCampaigns: {type: Array, default: () => []},
    contentHubItems: {type: Array, default: () => []},
  },
  data() {
    return {
      fields: {
        detail_background_type: this.dbRecord.detail_background_type || 'image',
        detail_background_color: this.dbRecord.detail_background_color || '#02b8a2',
        detail_background_image: null,
        detail_background_image_delete: false,
        title_area_type: this.dbRecord.title_area_type || 'image',
        title_area_text: this.dbRecord.title_area_text || '',
        title_area_image: null,
        title_area_image_delete: false,
        description_before_enabled: this.dbRecord.description_before_enabled || false,
        description_before_heading: this.dbRecord.description_before_heading || '',
        description_before_body: this.dbRecord.description_before_body || '',
        description_after_enabled: this.dbRecord.description_after_enabled || false,
        description_after_heading: this.dbRecord.description_after_heading || '',
        description_after_body: this.dbRecord.description_after_body || '',
      },
      // Gate rendering <ckeditor> until the app's own head-loaded CKEDITOR is fully
      // ready, so ckeditor4-vue never has a reason to load its own (different, and
      // conflicting) build in the meantime.
      ckeditorReady: false,
    }
  },
  mounted() {
    // The wizard's "Weiter" button lives outside this Vue root (parent form is
    // wrapped in v-pre), so it reaches in through this global to trigger the save.
    window.rewardGridApp = this;
    this.waitForCkeditor();
  },
  methods: {
    waitForCkeditor() {
      if (window.CKEDITOR && CKEDITOR.status === 'loaded') {
        this.ckeditorReady = true;
        return;
      }

      setTimeout(this.waitForCkeditor, 50);
    },
    // A method, not a data property: ckeditor4-vue mutates the config object it's
    // given (sets config.on.instanceReady internally), so every <ckeditor> needs its
    // own fresh object here, not a shared reference all instances would clobber.
    editorConfig() {
      return {
        versionCheck: false,
        extraPlugins: 'emoji,colordialogx,colorbuttonx,lineheight',
        removePlugins: 'resize,elementspath,magicline',
        toolbar: [
          {name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'RemoveFormat', 'Undo', 'Redo']},
          {name: 'styles', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock']},
          {name: 'sizes', items: ['FontSize', 'lineheight']},
          {name: 'paragraph', items: ['NumberedList', 'BulletedList']},
          {name: 'color', items: ['TextColorX']},
          {name: 'emoji', items: ['EmojiPanel']},
          {name: 'links', items: ['Link', 'Unlink']},
        ],
        height: '120px',
      };
    },
    buildFormData() {
      const formData = new FormData();

      formData.append('reward_grid_detail_background_type', this.fields.detail_background_type);
      formData.append('reward_grid_detail_background_color', this.fields.detail_background_color || '');
      formData.append('reward_grid_detail_background_image_delete', this.fields.detail_background_image_delete ? '1' : '0');

      formData.append('reward_grid_title_area_type', this.fields.title_area_type);
      formData.append('reward_grid_title_area_text', this.fields.title_area_text || '');
      formData.append('reward_grid_title_area_image_delete', this.fields.title_area_image_delete ? '1' : '0');

      formData.append('reward_grid_description_before_enabled', this.fields.description_before_enabled ? '1' : '0');
      formData.append('reward_grid_description_before_heading', this.fields.description_before_heading || '');
      formData.append('reward_grid_description_before_body', this.fields.description_before_body || '');

      formData.append('reward_grid_description_after_enabled', this.fields.description_after_enabled ? '1' : '0');
      formData.append('reward_grid_description_after_heading', this.fields.description_after_heading || '');
      formData.append('reward_grid_description_after_body', this.fields.description_after_body || '');

      this.$refs.tiles.appendToFormData(formData);

      return formData;
    },
    async save() {
      const response = await axios.post(this.saveUrl(), this.buildFormData());

      this.$refs.tiles.assignIds(response.data.tile_ids);
      await this.uploadPendingMedia();
    },
    saveUrl() {
      return `/backend/v3_campaigns/${this.campaignId}/reward_grid`;
    },
    pendingMedia() {
      const own = ['detail_background_image', 'title_area_image']
        .filter(collection => this.fields[collection])
        .map(collection => ({tileId: null, collection, file: this.fields[collection]}));

      return [...own, ...this.$refs.tiles.pendingMedia()];
    },
    // One image per request: PHP silently drops everything beyond max_file_uploads (20).
    async uploadPendingMedia() {
      for (const media of this.pendingMedia()) {
        await this.uploadMedia(media);
      }
    },
    uploadMedia({tileId, collection, file}) {
      const formData = new FormData();

      if (tileId) {
        formData.append('tile_id', tileId);
      }

      formData.append('collection', collection);
      formData.append('file', file);

      return axios.post(`${this.saveUrl()}/media`, formData);
    },
  },
}
</script>

<style scoped>
.rg-section {
  padding: 16px 0;
  border-bottom: 1px solid #e5e5e5;
}

.rg-section:last-child {
  border-bottom: none;
}

.rg-section__title {
  font-weight: 700;
  margin: 0 0 16px;
}

.rg-label {
  display: block;
  font-weight: 700;
  margin: 16px 0 8px;
}

.rg-tooltip-icon {
  cursor: pointer;
  color: #007bff;
}

.rg-color-input {
  display: block;
  width: 100%;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 4px;
}

.rg-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rg-checkbox-label {
  font-weight: 700;
  margin: 0;
  cursor: pointer;
}
</style>
